import React, { forwardRef, useCallback, useState, useEffect } from 'react'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'

import hayProductionCropManagementSchema from '@smartcoop/forms/schemas/management/hayProductionCropManagement.schema'
import { useT } from '@smartcoop/i18n'
import Form from '@smartcoop/web-components/Form'
import InputNumber from '@smartcoop/web-components/InputNumber'
import InputUnit from '@smartcoop/web-components/InputUnit'

const HayProductionForm = forwardRef(({ defaultValues, handleSubmit, readOnly, shouldDisplayCosts, area, growingSeasonCosts, concludeManagement }, formRef) => {
  const t = useT()

  const [projectedReceipt, setProjectedReceipt] = useState(null)

  const calculateProjectedReceipt = useCallback(() => {
    if (!shouldDisplayCosts) return
    const balesRollsPerHa = formRef?.current?.getFieldValue?.('balesRollsPerHa')
    const baleCotation = toNumber(formRef?.current?.getFieldValue?.('baleCotation'))

    if(isEmpty(toString(balesRollsPerHa)) || isEmpty(toString(baleCotation))) {
      setProjectedReceipt(null)
      return
    }
    setProjectedReceipt(toString(balesRollsPerHa * area * baleCotation))
  }, [area, formRef, shouldDisplayCosts])

  useEffect(() => {
    if(formRef.current && defaultValues?.balesRollsPerHa) {
      calculateProjectedReceipt()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, formRef.current])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ hayProductionCropManagementSchema }
      containerStyle={ { height: 'auto' } }
      onSubmit={ handleSubmit }
      schemaProps={ { concludeManagement } }
    >
      <InputNumber
        name="balesRollsPerHa"
        label={ t('number of bales/rolls per ha') }
        maxLength={ 3 }
        type="integer"
        fullWidth
        defaultValue={ defaultValues?.balesRollsPerHa }
        disabled={ readOnly }
        onChange={ () => {
          calculateProjectedReceipt()
        } }
      />

      <InputUnit
        name="balesWeightRollsPerHa"
        label={ t('Bale / hay roll weight') }
        maxLength={ 3 }
        unit="kg/fardo"
        type="integer"
        fullWidth
        defaultValue={ defaultValues?.balesWeightRollsPerHa }
        disabled={ readOnly }
        onChange={ () => {
          calculateProjectedReceipt()
        } }
      />

      {shouldDisplayCosts && (
        <>
          <InputUnit
            name="baleCotation"
            label={ t('bale cotation') }
            unit={ t('R$/fardo') }
            decimalScale={ 2 }
            type="float"
            fullWidth
            onChange={ calculateProjectedReceipt }
            defaultValue={ defaultValues?.baleCotation }
            disabled={ readOnly }
          />

          <InputUnit
            key={ `receipt-${ projectedReceipt }` }
            detached
            label={ t('projected receipt') }
            value={ toNumber(projectedReceipt) }
            unit={ t('R$') }
            decimalScale={ 2 }
            type="float"
            fullWidth
            disabled
          />

          <InputUnit
            detached
            label={ t('expected result') }
            value={ toNumber(projectedReceipt || '0')  + toNumber(growingSeasonCosts?.productionCost || '0')  }
            unit={ t('R$') }
            decimalScale={ 2 }
            type="float"
            fullWidth
            disabled
          />

          <InputUnit
            detached
            label={ t('expected result by ha') }
            value={ (toNumber(projectedReceipt || '0')  + toNumber(growingSeasonCosts?.productionCost || '0')) / area  }
            unit={ t('R$/ha') }
            decimalScale={ 2 }
            type="float"
            fullWidth
            disabled
          />
        </>
      )}
    </Form>
  )
})

HayProductionForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  shouldDisplayCosts: PropTypes.bool,
  concludeManagement: PropTypes.bool,
  area: PropTypes.number.isRequired,
  defaultValues: PropTypes.object,
  growingSeasonCosts: PropTypes.object
}

HayProductionForm.defaultProps = {
  readOnly: false,
  concludeManagement: false,
  shouldDisplayCosts: false,
  defaultValues: {},
  growingSeasonCosts: {}
}

export default HayProductionForm
